import { Badge, Button, Field, Input, Select } from '@bb/ui';
import { useId, useState } from 'react';
import { ApiError, type TemplateVariable } from '../../../api/client.ts';
import { ContentBlockPicker } from '../../content-blocks/ContentBlockPicker.tsx';
import { filterVariables, formatExample, groupByNamespace, namespaceLabel } from './personalization.ts';
import { useTemplateVariables } from './useTemplateVariables.ts';

export interface VariablePickerProps {
  environmentId: string;
  /** The campaign being edited: an action-based one adds its trigger events' properties, an API-triggered one API trigger properties. */
  campaignId?: string;
  /** The variable chosen (its `liquid` is the snippet to insert). */
  onSelect: (variable: TemplateVariable) => void;
  /** Focuses the search when the picker appears (it opens from a button). */
  autoFocus?: boolean;
  /** Variables of this field only, listed first (a webhook body template's `params`, BB-142). */
  extraVariables?: readonly TemplateVariable[];
  /** Offers content blocks too (BB-57): the text to insert, a block's link tag or a copy of its body. */
  onInsertText?: (text: string) => void;
}

function VariableItem({ variable, onSelect, detailsId }: { variable: TemplateVariable; onSelect: (variable: TemplateVariable) => void; detailsId: string }) {
  const details = [
    <Badge key="type">{variable.type}</Badge>,
    variable.piiLevel === 'pii' && (
      <Badge key="pii" tone="warning">
        PII
      </Badge>
    ),
    variable.event && (
      <span key="event" className="hint">
        from {variable.event}
      </span>
    ),
    variable.example !== undefined && (
      <span key="example" className="hint">
        e.g. {formatExample(variable.example)}
      </span>
    ),
    variable.description && (
      <span key="description" className="hint">
        {variable.description}
      </span>
    ),
  ].filter(Boolean);
  return (
    <li className="variable-picker__item">
      <button type="button" className="variable-picker__choose" aria-describedby={detailsId} onClick={() => onSelect(variable)}>
        <code>{variable.path}</code>
      </button>
      <span id={detailsId} className="variable-picker__details">
        {details.flatMap((part, i) => (i ? [' ', part] : [part]))}
      </span>
    </li>
  );
}

/**
 * Personalization variables of a message (BB-54): profile attributes, campaign data and — when the campaign has a
 * trigger — event or API trigger properties, grouped by namespace with a search. Each shows its type, an example and
 * whether it is personal data; choosing one hands it to `onSelect`.
 */
export function VariablePicker({ environmentId, campaignId, onSelect, autoFocus = false, extraVariables = [], onInsertText }: VariablePickerProps) {
  const id = useId();
  const catalog = useTemplateVariables(environmentId, campaignId);
  const [query, setQuery] = useState('');
  const [namespace, setNamespace] = useState('');
  const variables = [...extraVariables, ...(catalog.data?.variables ?? [])];
  const namespaces = [...new Set([...extraVariables.map((v) => v.namespace), ...(catalog.data?.namespaces ?? [])])];
  const groups = groupByNamespace(filterVariables(variables, query, namespace), namespaces);
  const present = groupByNamespace(variables, namespaces).map((g) => g.namespace);
  const first = groups[0]?.variables[0];
  const noTrigger = catalog.data && !namespaces.includes('event_properties') && !namespaces.includes('api_trigger_properties');

  return (
    <div className="variable-picker" role="group" aria-label="Personalization variables">
      <div className="filters">
        <Field label="Search variables">
          <Input
            type="search"
            value={query}
            placeholder="first_name, symbol…"
            autoFocus={autoFocus}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && first) {
                e.preventDefault();
                onSelect(first);
              }
            }}
          />
        </Field>
        {present.length > 1 && (
          <Field label="Namespace">
            <Select options={[{ value: '', label: 'All' }, ...present.map((n) => ({ value: n, label: namespaceLabel(n) }))]} value={namespace} onChange={(e) => setNamespace(e.target.value)} />
          </Field>
        )}
      </div>
      {catalog.isPending && <p aria-busy="true">Loading variables…</p>}
      {catalog.isError && (
        <div role="alert">
          <p>The variables could not be loaded{catalog.error instanceof ApiError ? `: ${catalog.error.message}` : '.'}</p>
          <Button size="sm" onClick={() => void catalog.refetch()}>
            Try again
          </Button>
        </div>
      )}
      {catalog.data && groups.length === 0 && <p className="hint">No variables match “{query.trim()}”.</p>}
      <div className="variable-picker__list">
        {groups.map((group) => (
          <section key={group.namespace} aria-label={namespaceLabel(group.namespace)}>
            <h4>
              {namespaceLabel(group.namespace)} <span className="hint">{group.variables.length}</span>
            </h4>
            <ul className="plain-list">
              {group.variables.map((v, i) => (
                <VariableItem key={`${v.namespace}.${v.path}.${v.event ?? ''}`} variable={v} onSelect={onSelect} detailsId={`${id}-${group.namespace}-${i}`} />
              ))}
            </ul>
          </section>
        ))}
        {onInsertText && <ContentBlockPicker environmentId={environmentId} query={query} onInsert={onInsertText} />}
      </div>
      {variables.some((v) => v.piiLevel === 'pii') && <p className="hint">PII values show as ••• in previews for employees without the pii.view permission.</p>}
      {noTrigger && <p className="hint">Trigger data is offered in action-based campaigns (event properties) and API-triggered ones (API trigger properties).</p>}
    </div>
  );
}
